import { getLogger } from '@logtape/logtape';
import axios, { AxiosError, AxiosResponse } from 'axios';

import { ApiError, api } from '@/lib/api-client.ts';

import { Document, DocumentResponseDto, DocumentType } from '@/models/documents/Document.ts';
import { ErrorResponseDto } from '@/models/documents/document-error-dto.ts';
import { GetUserDocExchangeNotificationsResponse } from '@/models/documents/document-exchange.ts';

import { documentUploadResponseSchema } from '@/schemas/document-upload-response.schema.ts';

import { ServiceUrl } from '@/services/ServiceURL.ts';

import { getCustomerId } from '@/utils/customer-utils.ts';
import { sanitizeFileName } from '@/utils/file-utils.ts';
import { serializeApiError } from '@/utils/log-utils.ts';

const logger = getLogger(['service', 'documents']);

interface DocumentsByUserResponse {
    data: Document[];
}

export const getDocumentsByUser = async (user_id: string, appointment_id?: string): Promise<Document[] | undefined> => {
    const config = {
        headers: {
            customer_id: getCustomerId(),
        },
        params: { user_id, appointment_id },
    };

    try {
        const response: AxiosResponse<DocumentsByUserResponse> = await api.get(
            `${ServiceUrl.PATIENT_API_DOMAIN.SECURE_ADMIN.USER.DOCUMENTS_DATA}/shared`,
            config
        );
        return response.data?.data;
    } catch (error) {
        logger.debug('Failed to fetch the documents shared by the user', { error: error, ...serializeApiError(error) });
        return undefined;
    }
};

export const getDocumentDownloadUrl = async (
    user_id: string,
    document_id: string
): Promise<[boolean, DocumentResponseDto | undefined, ApiError<ErrorResponseDto> | undefined]> => {
    const config = {
        headers: {
            customer_id: getCustomerId(),
        },
        params: { user_id: user_id },
    };

    try {
        const response = await api.get(
            `${ServiceUrl.PATIENT_API_DOMAIN.SECURE_ADMIN.USER.DOCUMENTS_DATA}/${document_id}`,
            config
        );
        return [true, response.data, undefined];
    } catch (error) {
        logger.debug('Failed to fetch the document download url', { error: error, ...serializeApiError(error) });
        return [false, undefined, error as ApiError<ErrorResponseDto>];
    }
};

export const generateDocumentDownloadCode = async (
    email: string
): Promise<[boolean, ApiError<ErrorResponseDto> | undefined]> => {
    const config = {
        headers: {
            customer_id: getCustomerId(),
        },
    };

    try {
        await api.post(
            `${ServiceUrl.PATIENT_API_DOMAIN.SECURE_ADMIN.USER.DOCUMENTS_DATA}/generate-download-code`,
            { email: email },
            config
        );
        return [true, undefined];
    } catch (error) {
        logger.debug('Failed to generate the document download code', { error: error, ...serializeApiError(error) });

        return [false, error as ApiError<ErrorResponseDto>];
    }
};

export const validateDocumentDownloadCode = async (
    code: string
): Promise<[boolean, ApiError<ErrorResponseDto> | undefined]> => {
    const config = {
        headers: {
            customer_id: getCustomerId(),
        },
    };

    try {
        await api.post(
            `${ServiceUrl.PATIENT_API_DOMAIN.SECURE_ADMIN.USER.DOCUMENTS_DATA}/validate-download-code`,
            { code: code },
            config
        );
        return [true, undefined];
    } catch (error) {
        logger.debug('Failed to validate the document download code', { error: error, ...serializeApiError(error) });
        return [false, error as ApiError<ErrorResponseDto>];
    }
};

export const getUserDocExchangeNotifications = async (
    pvs_user_id: string,
    user_instance_id: string
): Promise<GetUserDocExchangeNotificationsResponse | undefined> => {
    try {
        const api_url = `${ServiceUrl.PATIENT_API_DOMAIN.SECURE_ADMIN.USER.DOCUMENTS_DATA}/unregistered-notifications`;

        const response = await api.get<GetUserDocExchangeNotificationsResponse>(api_url, {
            params: {
                pvs_user_id: pvs_user_id,
                instance_id: user_instance_id,
            },
            headers: {
                'Eh-Customer-Id': getCustomerId(),
            },
        });

        return response.data;
    } catch (error) {
        logger.debug('Failed to get user document exchange notifications', {
            error: error,
            ...serializeApiError(error),
        });
        return undefined;
    }
};

export const resendDocumentExchangeNotification = async (
    pvs_user_id: string,
    document_id: string,
    user_instance_id: string
): Promise<boolean> => {
    try {
        const api_url = `${ServiceUrl.PATIENT_API_DOMAIN.SECURE_ADMIN.USER.DOCUMENTS_DATA}/unregistered-notifications/resend`;

        await api.post(
            api_url,
            {
                document_id,
            },
            {
                params: {
                    pvs_user_id: pvs_user_id,
                    instance_id: user_instance_id,
                },
                headers: {
                    'Eh-Customer-Id': getCustomerId(),
                },
            }
        );

        return true;
    } catch (error) {
        logger.debug('Failed to resend document exchange notification', { error: error, ...serializeApiError(error) });
        throw error;
    }
};

export interface UploadDocumentParams {
    userId: string;
    file: File;
    documentType: DocumentType;
    appointmentId?: string;
    instanceId?: string;
}

export const uploadDocumentForUser = async ({
    userId,
    file,
    documentType,
    appointmentId,
    instanceId,
}: UploadDocumentParams): Promise<void> => {
    const requestBody = {
        user_id: userId,
        type: documentType,
        mime_type: file.type,
        name: sanitizeFileName(file.name),
        appointment_id: appointmentId,
        content_length: file.size,
    };

    try {
        const uploadUrlResponse = await api.post(
            ServiceUrl.PATIENT_API_DOMAIN.SECURE_ADMIN.USER.DOCUMENTS_DATA,
            requestBody,
            {
                params: { instance_id: instanceId },
                headers: {
                    'Content-Type': 'application/json',
                    'Eh-Customer-Id': getCustomerId(),
                },
            }
        );

        const parsed = documentUploadResponseSchema.safeParse(uploadUrlResponse.data);
        if (!parsed.success) {
            throw new ApiError(500, 'Ungültige Antwort vom Server beim Erstellen des Dokuments.');
        }

        const { upload_url: uploadUrl } = parsed.data;
        await axios.put(uploadUrl, file, {
            headers: {
                'Content-Type': file.type,
            },
        });
    } catch (error) {
        if (error instanceof ApiError) {
            throw error;
        }

        const status = (error as AxiosError)?.response?.status ?? 500;
        throw new ApiError(status, 'Dokument konnte nicht hochgeladen werden.');
    }
};
